import { firebaseAuth, firebaseDatabase } from './firebase';
import { getCachedCollection, readCollection } from './collection-cache';
import bcrypt from 'bcryptjs';
import { config } from './config';
import { cocTimestamp, submittedAt, joinedAt, companyNameForCoc, companiesByUser, countedForLab, toMillis, withDefaultOffice } from '../utils/coc-data';


/**
 * Makes an entry to firebase authentication for an anonymous user logged In..
 * Returns a promise..
 */
export const loginAnonymously = () => {
    return new Promise((resolve, reject) => {

        firebaseAuth.signInAnonymously()
            .then(data => {
                // console.log('User Data', data)
                resolve(data)
            })
            .catch(error => {
                reject(error)
            });
    })
}

/**
 * Resolves once a Firebase auth session exists; signs in anonymously when the
 * persisted session is missing or expired. Deployed rules require auth, so
 * Firestore reads must wait for this after a reload (auth_token alone is not enough).
 */
let authRequest;
export const ensureFirebaseAuth = () => {
    if (firebaseAuth.currentUser) return Promise.resolve(firebaseAuth.currentUser);
    if (!authRequest) {
        authRequest = new Promise((resolve, reject) => {
            const unsubscribe = firebaseAuth.onAuthStateChanged(user => {
                unsubscribe();
                if (user) resolve(user);
                else firebaseAuth.signInAnonymously().then(data => resolve(data.user)).catch(reject);
            }, reject);
        }).finally(() => { authRequest = null; });
    }
    return authRequest;
}

/**
 * Observer that gets callback for when user logs in or logs out..
 */
export const registerFirebaseAuthChanged = () => {
    return new Promise((resolve, reject) => {

        firebaseAuth.onAuthStateChanged(user => {
            if (user) {
                resolve('loggedIn')
            } else {
                reject('loggedOut')
            }
        })
    })
}

/**
 * Retrives users from the firebase database and checks for user trying to get logged in into..
 * Returns a promise..
 * @param {String} email 
 * @param {String} password 
 */
export const checkForLogin = (email, password) => {
    const db = firebaseDatabase;
    return new Promise((resolve, reject) => {


        db.collection('Admins').where('email', '==', email).get().then((querySnapshot) => {
            if (!querySnapshot.empty && querySnapshot.docs.length === 1) {
                querySnapshot.forEach((doc) => {
                    const user = doc.data();
                    if (user.email && user.email === email && user.isAdmin) {
                        if (user.password) {
                            const dbPassword = user.password;
                            const res = bcrypt.compareSync(password, dbPassword);
                            if (res) {
                                resolve({
                                    status: 'Success',
                                    message: 'Login Success'
                                })
                            }
                            else {
                                reject({
                                    status: 'Failure',
                                    message: 'Wrong Password'
                                })
                            }
                        } else {
                            reject({
                                status: 'Failure',
                                message: 'Wrong Password'
                            })
                        }
                    }
                })
                reject({
                    status: 'Failure',
                    message: 'Unauthorize user'
                })
            }
            else {
                reject({
                    status: 'Failure',
                    message: 'Unauthorize user'
                })
            }
        }).catch((error) => {
            console.log("Error getting document:", error);
        })


    })
}



export const checkAanalysisCategoriesDuplicacy = (categoryName) => {
    return new Promise((resolve, reject) => {
        firebaseDatabase.collection('AnalysisCategories').where('name', '==', categoryName).get().then((querySnapshot) => {
            if (!querySnapshot.empty) {
                reject({
                    status: 'Failure',
                    message: 'Category already exists'
                });
            }
            else {
                resolve({
                    status: 'Success',
                    message: 'New Record'
                });
            }

        })
    })

}


export const checkTestDuplicacy = (analysisType, analysisCategoryId, cocTypeId) => {
    return new Promise((resolve, reject) => {
        firebaseDatabase.collection('Tests').where('analysis_type', '==', analysisType).where('analysis_category_id', '==', analysisCategoryId).where('coc_type_id', '==', cocTypeId).get().then((querySnapshot) => {
            if (!querySnapshot.empty) {
                reject({
                    status: 'Failure',
                    message: 'Test already exists'
                });
            }
            else {
                resolve({
                    status: 'Success',
                    message: 'New Record'
                });
            }

        })
    })

}

export const createLab = async (labDetails, testConducted) => {
    if (testConducted.length > 499) throw new Error('A lab can save at most 499 analysis categories at once.');
    const ref = firebaseDatabase.collection('Laboratory').doc();
    const batch = firebaseDatabase.batch();
    const timestamp = Math.round(Date.now() / 1000);
    batch.set(ref, { ...labDetails, status: true, createdAt: timestamp, updatedAt: timestamp, cocCount: 0 });
    testConducted.forEach(test => batch.set(ref.collection('TestConducted').doc(), test));
    await batch.commit();
    return { status: 'Success', message: 'Lab created successfully' };
};

export const updateLabStatus = (currentStatus, labId) => {
    const db = firebaseDatabase;
    return new Promise((resolve, reject) => {
        const currentTimestamp = Math.round(Date.now() / 1000);
        db.collection('Laboratory').doc(labId).update({
            status: !currentStatus,
            updatedAt: currentTimestamp,
        }).then(() => {
            resolve({
                status: 'Success',
                message: 'Lab updated successfully'
            })
        }).catch((error) => {
            if (error.code === 'not-found')
                reject({
                    status: 'Failure',
                    message: 'Lab not found'
                })
            else {
                reject({
                    status: 'Failure',
                    message: 'Something went wrong'
                })
            }
        })
    })

}

export const approveLabReview = (labId) => {
    const db = firebaseDatabase;
    return new Promise((resolve, reject) => {
        const currentTimestamp = Math.round(Date.now() / 1000);
        db.collection('Laboratory').doc(labId).update({
            status: true,
            isUnderReview: false,
            updatedAt: currentTimestamp,
        }).then(() => {
            resolve({ status: 'Success', message: 'Lab approved successfully' });
        }).catch(() => {
            reject({ status: 'Failure', message: 'Something went wrong' });
        });
    });
};

export const getLabBasicInfo = (labId) => {
    const db = firebaseDatabase;
    return new Promise((resolve, reject) => {
        db.collection('Laboratory').doc(labId).get()
            .then((doc) => {
                console.log('Lab Details.....', doc.data())
                resolve({
                    status: 'Success',
                    message: 'Data received succeessfully',
                    lab: doc.data()
                })
            }).catch((error) => {
                console.log('Lab details fetching error', error);
                reject({
                    status: 'Failure',
                    message: 'Something went wrong '
                })
            })
    })
}


export const getLabConductedTestsOld = (labId) => {
    const db = firebaseDatabase;
    return new Promise((resolve, reject) => {
        db.collection('Laboratory').doc(labId).collection('TestConducted').get()
            .then((querySnapshot) => {
                const test = [];
                querySnapshot.forEach((doc) => {
                    test.push({ testId: doc.id, ...doc.data() });
                })
                resolve({
                    status: 'success',
                    message: 'old test received',
                    oldTest: test
                })
            }).catch((error) => {
                console.log('Error test fetch data ', error);
                reject({
                    status: 'failure',
                    message: 'Something went wrong'
                })
            })
    })
}


export const updateLab = async (labDetails, testConducted, labId, oldTestIds) => {
    if (testConducted.length + oldTestIds.length > 499) throw new Error('Too many analysis categories to update atomically.');
    const ref = firebaseDatabase.collection('Laboratory').doc(labId);
    const batch = firebaseDatabase.batch();
    batch.update(ref, { ...labDetails, updatedAt: Math.round(Date.now() / 1000) });
    testConducted.forEach(test => batch.set(ref.collection('TestConducted').doc(), test));
    oldTestIds.forEach(id => batch.delete(ref.collection('TestConducted').doc(id)));
    await batch.commit();
    return { status: 'Success', message: 'Lab updated successfully' };
};

export const getCocsByLabIdWithDateFilter = async (labId, startDate = 0, endDate = 9999999999999, lab = null) => {
    const [snapshot, companySnap, projectsSnap] = await Promise.all([
        firebaseDatabase.collection('COC').where('labId', '==', labId).get(),
        firebaseDatabase.collection('CompanyDetails').get(),
        readCollection('Projects'),
    ]);
    const companies = Object.fromEntries(companySnap.docs.map(d => [d.id, { ...d.data(), id: d.id }]));
    const companyByUser = companiesByUser(Object.values(companies));
    const owners = Object.fromEntries(projectsSnap.docs.map(d => [d.id, d.data().userId]));
    const cocS = await Promise.all(snapshot.docs.map(d => ({ ...d.data(), id: d.id }))
        .map(coc => withDefaultOffice(coc, companyByUser[coc.userId || owners[coc.projectId]]))
        .filter(c => countedForLab(c, lab))
        .filter(c => { const ts = submittedAt(c); return ts ? ts >= startDate && ts <= endDate : startDate === 0; })
        .sort((a, b) => submittedAt(b) - submittedAt(a) || toMillis(b.createdAt) - toMillis(a.createdAt))
        .map(async coc => ({ ...coc, performingOfficeCompanyName: companyNameForCoc(coc, companies), numberOfSamples: (await getSamplesByCOCId(coc.id)).samplesSize })));
    return { status: 'success', cocS };
};


export const getCocsByLabId = (labId) => {
    const db = firebaseDatabase;
    return new Promise((resolve, reject) => {
        db.collection('COC').where('labId', '==', labId).get()
            .then((querySnapshot) => {
                resolve({
                    status: 'success',
                    message: 'Cocs received',
                    cocSize: querySnapshot.size
                })
            }).catch((error) => {
                console.log('Error Cocs fetch', error);
                reject({
                    status: 'failure',
                    message: 'Something went wrong'
                })
            })
    })

}


export const getUserProfileById = async userId => {
    const [user, billing, companies, createTimes] = await Promise.all([
        firebaseDatabase.collection('Users').doc(userId).get(),
        firebaseDatabase.collection('UserBilling').doc(userId).get(),
        firebaseDatabase.collection('CompanyDetails').where('userId', '==', userId).get(),
        getUserCreateTimes(userId),
    ]);
    if (!user.exists) throw new Error('User not found.');
    const company = companies.docs[0];
    return { user: { ...user.data(), id: user.id, documentCreatedAt: createTimes[user.id] }, billing: billing.exists ? billing.data() : {}, company: company ? { ...company.data(), id: company.id } : {} };
};

export const getCocsByUserId = async (userId) => {
    const cached = ['Projects', 'COC', 'Laboratory', 'CompanyDetails'].map(getCachedCollection);
    if (cached.every(Boolean)) {
        const [projects, cocs, labs, companies] = cached;
        const projectIds = new Set(projects.docs.filter(doc => doc.data().userId === userId).map(doc => doc.id));
        const company = companiesByUser(companies.docs.map(doc => ({ ...doc.data(), id: doc.id })))[userId];
        const rows = cocs.docs.map(doc => ({ ...doc.data(), id: doc.id }))
            .filter(coc => coc.userId === userId || projectIds.has(coc.projectId))
            .map(coc => withDefaultOffice(coc, company))
            .sort((a, b) => cocTimestamp(b) - cocTimestamp(a));
        const labIds = new Set(rows.map(coc => coc.labId));
        return {
            cocs: rows,
            labs: Object.fromEntries(labs.docs.filter(doc => labIds.has(doc.id)).map(doc => [doc.id, { ...doc.data(), id: doc.id }])),
            companies: Object.fromEntries(companies.docs.map(doc => [doc.id, { ...doc.data(), id: doc.id }])),
        };
    }
    const db = firebaseDatabase;
    // Step 1: get all projects for this user
    const projectsSnap = await db.collection('Projects').where('userId', '==', userId).get();

    const projectIds = projectsSnap.docs.map(d => d.id);

    // Step 2: fetch COCs in batches of 10 (Firestore 'in' limit)
    const batches = [];
    for (let i = 0; i < projectIds.length; i += 10) {
        batches.push(projectIds.slice(i, i + 10));
    }
    const cocDocs = [];
    await Promise.all(batches.map(async batch => {
        const snap = await db.collection('COC').where('projectId', 'in', batch).get();
        snap.docs.forEach(d => cocDocs.push({ id: d.id, ...d.data() }));
    }));

    const directSnap = await db.collection('COC').where('userId', '==', userId).get();
    directSnap.docs.forEach(d => { if (!cocDocs.some(c => c.id === d.id)) cocDocs.push({ ...d.data(), id: d.id }); });
    const companiesSnap = await db.collection('CompanyDetails').get();
    const companies = Object.fromEntries(companiesSnap.docs.map(d => [d.id, { ...d.data(), id: d.id }]));

    // Step 3: fetch lab details for unique labIds
    const labIds = [...new Set(cocDocs.map(c => c.labId).filter(Boolean))];
    const labs = {};
    await Promise.all(labIds.map(async labId => {
        const labDoc = await db.collection('Laboratory').doc(labId).get();
        if (labDoc.exists) labs[labId] = { id: labId, ...labDoc.data() };
    }));

    cocDocs.sort((a, b) => cocTimestamp(b) - cocTimestamp(a) || toMillis(b.createdAt) - toMillis(a.createdAt));
    const company = companiesByUser(Object.values(companies))[userId];
    return { cocs: cocDocs.map(coc => withDefaultOffice(coc, company)), labs, companies };
};

const getSamplesByCOCId = (cocId) => {
    const db = firebaseDatabase;
    return new Promise((resolve, reject) => {
        db.collection('Samples').where('cocId', '==', cocId).get()
            .then((querySnapshot) => {
                resolve({
                    status: 'success',
                    message: 'Samples received',
                    samplesSize: querySnapshot.size
                })
            }).catch((error) => {
                console.log('Error Samples fetch', error);
                reject({
                    status: 'failure',
                    message: 'Something went wrong'
                })
            })
    })

}

// ─── Users + Billing ─────────────────────────────────────────────────────────

// The mobile app stores no signup date on Users documents. Firestore keeps each document's creation
// time, but the web SDK does not expose it, so it is read over REST. Returns { userId: millis };
// empty when unavailable, in which case Joined stays blank.
export const getUserCreateTimes = async (userId = '') => {
    const times = {};
    try {
        const token = await firebaseAuth.currentUser?.getIdToken();
        if (!token) return times;
        const base = `https://firestore.googleapis.com/v1/projects/${config.projectId}/databases/(default)/documents/Users`;
        let pageToken = '';
        do {
            const url = userId ? `${base}/${userId}?mask.fieldPaths=email` : `${base}?pageSize=300&mask.fieldPaths=email${pageToken ? `&pageToken=${encodeURIComponent(pageToken)}` : ''}`;
            const response = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
            if (!response.ok) break;
            const body = await response.json();
            (userId ? [body] : body.documents || []).forEach(doc => { times[doc.name.split('/').pop()] = Date.parse(doc.createTime) || 0; });
            pageToken = userId ? '' : body.nextPageToken || '';
        } while (pageToken);
    } catch (error) {
        console.log('User creation times unavailable', error);
    }
    return times;
};

export const getAllUsers = () => {
    return new Promise((resolve, reject) => {
        Promise.all([readCollection('Users'), getUserCreateTimes()])
            .then(([snapshot, createTimes]) => {
                const users = snapshot.docs
                    .map(doc => ({ id: doc.id, ...doc.data(), documentCreatedAt: createTimes[doc.id] }))
                    .sort((a, b) => {
                        const tsA = joinedAt(a);
                        const tsB = joinedAt(b);
                        return tsB - tsA;
                    });
                resolve({ status: 'success', users });
            })
            .catch(err => reject({ status: 'failure', message: err.message }));
    });
};

export const getAllUserBilling = () => {
    return new Promise((resolve, reject) => {
        readCollection('UserBilling')
            .then(snapshot => {
                const billingMap = {};
                snapshot.docs.forEach(doc => {
                    billingMap[doc.id] = { id: doc.id, ...doc.data() };
                });
                resolve({ status: 'success', billingMap });
            })
            .catch(err => reject({ status: 'failure', message: err.message }));
    });
};

export const setUserAdminFree = (userId, isAdminFree) => {
    return firebaseDatabase.collection('UserBilling').doc(userId).set(
        { isAdminFree, lastUpdated: Date.now() },
        { merge: true }
    );
};

// resetAt = 0 undoes the reset and counts every COC again.
export const setLabCocCountReset = (labId, resetAt) => {
    return firebaseDatabase.collection('Laboratory').doc(labId).update({ cocCountResetAt: resetAt, updatedAt: Math.round(Date.now() / 1000) });
};

export const setLabPaysCOCs = (labId, labPaysForCOCs) => {
    return firebaseDatabase.collection('Laboratory').doc(labId).set(
        { labPaysForCOCs, updatedAt: Date.now() },
        { merge: true }
    );
};

export const deleteUserById = (userId) => {
    const db = firebaseDatabase;
    return Promise.all([
        db.collection('Users').doc(userId).delete(),
        db.collection('UserBilling').doc(userId).delete(),
    ]);
};

export const getAllCompanyDetails = () => {
    return new Promise((resolve, reject) => {
        readCollection('CompanyDetails')
            .then(snapshot => {
                const companyMap = {};
                snapshot.docs.forEach(doc => {
                    const data = doc.data();
                    if (data.userId) {
                        companyMap[data.userId] = { id: doc.id, ...data };
                    }
                });
                resolve({ status: 'success', companyMap });
            })
            .catch(err => reject({ status: 'failure', message: err.message }));
    });
};

export const grantCompanyPlanAdmin = (userId, userEmail, planTier, seatCount, renewalDateMs, existingCompanyId) => {
    const db = firebaseDatabase;
    const companyRef = existingCompanyId
        ? db.collection('Companies').doc(existingCompanyId)
        : db.collection('Companies').doc();
    const companyId = companyRef.id;
    const ops = [
        companyRef.set({
            adminUserId: userId,
            planTier,
            seatCountPurchased: seatCount,
            seatCountAssigned: 1,
            renewalDate: renewalDateMs,
            subscriptionPlatform: 'admin',
            subscriptionStatus: 'active',
            transactionId: `admin_grant_${Date.now()}`,
            ...(existingCompanyId ? {} : { createdAt: Date.now() }),
        }, { merge: true }),
        db.collection('UserBilling').doc(userId).set(
            { planType: 'unlimited_company', companyId, unlimitedExpiry: renewalDateMs, lastUpdated: Date.now() },
            { merge: true }
        ),
    ];
    if (!existingCompanyId) {
        ops.push(db.collection('company_users').add(
            { companyId, userEmail, userId, role: 'admin', active: true, assignedAt: Date.now() }
        ));
    }
    return Promise.all(ops).then(() => ({ companyId }));
};

// ─── Payments ────────────────────────────────────────────────────────────────

export const getAllPayments = () => {
    const db = firebaseDatabase;
    return new Promise((resolve, reject) => {
        db.collection('Payments').orderBy('createdAt', 'desc').get()
            .then(snapshot => {
                const payments = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                resolve({ status: 'success', payments });
            })
            .catch(err => reject({ status: 'failure', message: err.message }));
    });
};

// ─── Inquiries ────────────────────────────────────────────────────────────────

export const getAllInquiries = () => {
    const db = firebaseDatabase;
    return new Promise((resolve, reject) => {
        db.collection('Inquiries').orderBy('createdAt', 'desc').get()
            .then(snapshot => {
                const inquiries = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                resolve({ status: 'success', inquiries });
            })
            .catch(err => reject({ status: 'failure', message: err.message }));
    });
};

export const updateInquiryStatus = (inquiryId, status) => {
    return firebaseDatabase.collection('Inquiries').doc(inquiryId).update({ status });
};

// ─── Labs ─────────────────────────────────────────────────────────────────────

export const deleteLabById = (labId) => {
    return new Promise(async (resolve, reject) => {
        try {
            const db = firebaseDatabase;

            // Delete all TestConducted subcollection docs
            const subSnap = await db.collection('Laboratory').doc(labId).collection('TestConducted').get();
            const batch = db.batch();
            subSnap.forEach(doc => batch.delete(doc.ref));
            await batch.commit();

            // Delete the lab document itself
            await db.collection('Laboratory').doc(labId).delete();
            resolve({ status: 'success' });
        } catch (err) {
            reject({ message: err.message });
        }
    });
};

// ─── COC Types ────────────────────────────────────────────────────────────────

export const createCocType = (name) => {
    return new Promise((resolve, reject) => {
        firebaseDatabase.collection('CocTypes').add({ name })
            .then(docRef => resolve({ id: docRef.id, name }))
            .catch(err => reject({ message: err.message }));
    });
};

export const updateCocType = (cocTypeId, newName) => {
    return new Promise((resolve, reject) => {
        firebaseDatabase.collection('CocTypes').doc(cocTypeId).update({ name: newName })
            .then(() => {
                return firebaseDatabase.collection('Tests')
                    .where('coc_type_id', '==', cocTypeId)
                    .get();
            })
            .then(snapshot => {
                const batch = firebaseDatabase.batch();
                snapshot.forEach(doc => batch.update(doc.ref, { coc_type_name: newName }));
                return batch.commit();
            })
            .then(() => resolve({ status: 'success' }))
            .catch(err => reject({ message: err.message }));
    });
};

export const deleteCocType = (cocTypeId) => {
    return new Promise((resolve, reject) => {
        firebaseDatabase.collection('CocTypes').doc(cocTypeId).delete()
            .then(() => resolve({ status: 'success' }))
            .catch(err => reject({ message: err.message }));
    });
};

